import React, { useEffect, useMemo, useRef, useState } from 'react';

import { uniq } from 'lodash';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { FlightAirlineFilter, FlightFilter } from 'models/flightFilter';
import { getFormattedPrice } from 'utils/currency';

import {
  Airline,
  AirTable,
  Container,
  StopTable,
  Price,
  Border,
  PrevButton,
  NextButton,
  Content,
  Header,
} from './styles';

interface Props {
  airlines?: FlightAirlineFilter[];
  filters?: FlightFilter;
  currency: string;
  onClick: (filters: FlightFilter) => void;
}

const showPrice = (price?: number, currency?: string): string => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency, t });
    return formatted;
  };
  return formattedPrice(price);
};

const FlightsCompare = ({ airlines, filters, currency, onClick }: Props) => {
  const { t } = useStoreTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [scrollPos, setScrollPos] = useState(0);

  const stopsHeader = useMemo(() => {
    const stopsLabel = [
      `${t('flightsFilters.stops.direct')}`,
      `${t('flightsFilters.stops.oneStop')}`,
      `${t('flightsFilters.stops.twoOrMoreStops')}`,
    ];
    return airlines
      ? uniq(
          airlines
            .sort((a, b) => (a.stops && b.stops ? a.stops - b.stops : 0))
            .map((item) => (item.stops ? item.stops : 0))
            .map((item) => (item < 2 ? stopsLabel[item] : stopsLabel[2])),
        )
      : [];
  }, [airlines, t]);

  const stops = useMemo(() => uniq(airlines?.map((item) => (item.stops ? item.stops : 0))), [airlines]);
  interface GroupedItems {
    [key: string]: FlightAirlineFilter[];
  }
  const groupedItems = useMemo(() => {
    const grouped: GroupedItems = {};
    if (airlines) airlines.forEach((item) => (grouped[item.text] = [...(grouped[item.text] || []), item]));
    return grouped;
  }, [airlines]);

  const filteredItems = useMemo(() => Object.entries(groupedItems).filter(([k, v]) => k !== ''), [groupedItems]);

  useEffect(() => {
    setNextEnabled(false);
    setPrevEnabled(false);
    if (contentRef.current && tableRef.current) {
      if (contentRef.current.offsetWidth < tableRef.current.offsetWidth) {
        setNextEnabled(true);
      }
      if (scrollPos > 0) {
        setPrevEnabled(true);
      } else {
        setPrevEnabled(false);
      }
      if (scrollPos === contentRef.current.offsetWidth) {
        setNextEnabled(false);
      }
    }
    if (filteredItems.length <= 4) {
      setNextEnabled(false);
    }
  }, [groupedItems, stopsHeader, contentRef, tableRef, scrollPos, filteredItems.length]);

  const onClickLogo = (filter: FlightAirlineFilter) => {
    const newAirlines = [filter.text];
    const newFilters = {
      airlines: uniq(newAirlines),
    };
    onClick(newFilters);
  };
  const onClickTable = (filter: FlightAirlineFilter) => {
    const newAirlines: string[] = [filter.text];
    const newStops: string[] = [String(filter.stops)];
    const newFilters = {
      airlines: uniq(newAirlines),
      stops: uniq(newStops),
    };
    onClick(newFilters);
  };
  const onClickHeader = (label: string) => {
    let stops = 0;

    switch (label) {
      case t('flightsFilters.stops.direct'): {
        stops = 0;
        break;
      }
      case t('flightsFilters.stops.oneStop'): {
        stops = 1;
        break;
      }
      case t('flightsFilters.stops.twoOrMoreStops'): {
        stops = 2;
        break;
      }
    }

    const newStops = [String(stops)];
    const newFilters = {
      stops: uniq(newStops),
    };
    onClick(newFilters);
  };
  return (
    <Container>
      <Header>{t('flightsFilters.comparePrices')}</Header>
      <Content>
        <StopTable>
          <table>
            <thead>
              <tr>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {stopsHeader &&
                stopsHeader.map((item, idx) => (
                  <tr key={idx}>
                    <td onClick={() => onClickHeader(item)}>
                      <span>{item}</span>
                      <Border />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </StopTable>
        <AirTable ref={contentRef}>
          <table ref={tableRef}>
            <thead>
              <tr>
                {filteredItems &&
                  filteredItems.map(([k, v]) => (
                    <Airline key={k} onClick={() => onClickLogo(v[0])}>
                      <Airline.Div>
                        <Airline.Logo
                          src={
                            k === 'MultipleCarriers'
                              ? 'https://images.rappi.com/web/travel/airlines/multiple.png'
                              : `https://images.rappi.com/web/travel/airlines/${k}.png`
                          }
                          alt=""
                          loading="lazy"
                        />
                        <Airline.Text>
                          {v[0] && v[0]['airlineName']
                            ? v[0]['airlineName']
                            : k === 'MultipleCarriers'
                            ? t('flightsFilters.airlines.multiple')
                            : k}
                        </Airline.Text>
                      </Airline.Div>
                      <Border />
                    </Airline>
                  ))}
              </tr>
            </thead>
            <tbody>
              {stopsHeader &&
                Object.keys(stopsHeader).map((i, idx) => (
                  <tr key={idx}>
                    {filteredItems &&
                      filteredItems.map(([k, v]) => (
                        <td key={`${idx}-${k}`}>
                          {(v as FlightAirlineFilter[]).map((o, key) => (
                            <span key={key} onClick={() => onClickTable(o)}>
                              {o.stops === stops[idx] && (
                                <>
                                  <Price>{showPrice(Number(o.minPrice), currency)}</Price>
                                  <Border />
                                </>
                              )}
                            </span>
                          ))}
                        </td>
                      ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </AirTable>
        {prevEnabled && (
          <PrevButton
            onClick={() => {
              if (contentRef.current) {
                const pos = contentRef.current.scrollLeft - contentRef.current.offsetWidth;
                contentRef.current.scrollTo({ left: pos, top: 0, behavior: 'smooth' });
                setScrollPos(pos);
              }
            }}
          >
            <PrevButton.Icon />
          </PrevButton>
        )}
        {nextEnabled && (
          <NextButton
            onClick={() => {
              if (contentRef.current) {
                const pos = contentRef.current.scrollLeft + contentRef.current.offsetWidth;
                contentRef.current.scrollTo({ left: pos, top: 0, behavior: 'smooth' });
                setScrollPos(pos);
              }
            }}
          >
            <NextButton.Icon />
          </NextButton>
        )}
      </Content>
    </Container>
  );
};

export default FlightsCompare;
